import { Component, OnInit, OnChanges, EventEmitter, Input, Output } from '@angular/core';
import { AssigneeHelperService } from '../tibAssignee/trackeritem.browse.assignee.helper';
import { UserService } from '../../../../core/services/core.user.service';
import { TrackeritemService } from '../../services/trackeritem.service';
import * as _ from 'lodash';

@Component({
  selector: 'assignees-selector',
  template: require('./assignees.selector.component.html')
})
export class AssigneesSelectorComponent implements OnInit, OnChanges {
  @Input() members: any[];       //The initial page of members list
  @Input() trackerItem: any;     //Tracker item with (or without) and assignees list
  @Input() acls: any;            //Acls to activate or disable the selector
  @Input() options: any;         //Optional callback to pass configuration options
  @Input() version: any;         //Optional value, if "1" it renders the usual old styling. If "2" it renders the new styling applied to TI Edit
  @Output() update: EventEmitter<void> = new EventEmitter<void>();

  assigneesList: any[];
  membersList: any[];
  currentUser: any;
  allowSaving: boolean;
  allowEmpty: boolean;
  membersFilter: string = '';
  allowUpdates: boolean = false;

  private _assigneesHistoryList: any[];
  private debouncedGetFilteredAssignees: any;

  constructor(
    private assigneeHelper: AssigneeHelperService,
    private userService: UserService,
    private trackeritemService: TrackeritemService
  ) {}

  ngOnInit() {
    this._assigneesHistoryList = _.cloneDeep(_.get(this.trackerItem, 'rel.assignees', []));
    this.debouncedGetFilteredAssignees = this.debouncePromise(this.doSearch.bind(this), 100, { 'maxWait': 1000 });
    this.currentUser = this.userService.session.getUser();
    this.allowSaving = _.get(this.options, 'allowSaving', false);
    this.allowEmpty = _.get(this.options, 'allowEmpty', false);
    this.init();
    this.assigneesList = this.getAssigneesList();
  }

  ngOnChanges() {
    this.init();

    if (!this._assigneesHistoryList?.length) {
      this._assigneesHistoryList = _.cloneDeep(_.get(this.trackerItem, 'rel.assignees', []));
    }

    this.assigneesList = this.getAssigneesList();
    this.setMembersList(this.members);
  }

  /**
   * Check if user interaction should be disabled
   * @returns boolean
   */
  isDisabled(): boolean {
    return !this.acls.canAssign || !this.allowUpdates;
  }

  /**
   * Respond to filtering action in the selector
   */
  membersFilterUpdate() {
    if (this.isDisabled()) return;
    
    if (this.membersFilter?.length > 0) {
      this.debouncedGetFilteredAssignees(this.membersFilter)
        .then(() => {
          if (!this.membersFilter?.length) {
            this.setMembersList(this.members);
          }
        })
        .catch(err => {
          console.log(err);
        });
    } else {
      this.setMembersList(this.members);
    }
  }

  /**
   * Checks if given member is in the current
   * assignees list on the TI
   * 
   * @param {object} member 
   * @returns {boolean}
   */
  isAssignedTo(member: any): boolean {
    return this.assigneeHelper.isAssignedTo(this.trackerItem, member);
  }

  /**
    * Unassigns all current assignees and assigns
    * the session user. If available, it calls the save
    * callback to save the TI
    */
  assignToMe(ev?: Event) {
    if (ev) {
        ev.preventDefault();
        ev.stopPropagation();
    }

    this.assigneeHelper.emptyAssignees(this.trackerItem);
    this.assigneeHelper.setAssignee(this.trackerItem, this.currentUser);
    this.addToAssigneesHistoryList();
    this.saveAssignees();
  }

  /**
    * Unassignes everyone from the TI. If available, it calls the save
    * callback to save the TI
    */
  assignToNobody() {
    this.assigneeHelper.assignToNobody(this.trackerItem);
    this.addToAssigneesHistoryList();
    this.saveAssignees();
  }

  /**
    * Sets the given member as an
    * assignee to the TI
    * @param {object} member 
    */
  setAssignee(ev: any, member: any) {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.isDisabled()) return;

    this.assigneeHelper.setAssignee(this.trackerItem, member);
    this.update.emit(this.getAssigneesList());
    this.addToAssigneesHistoryList();
  }

  emptyAssignees() {
    if (!this.allowUpdates) return;
    this.assigneeHelper.emptyAssignees(this.trackerItem);
    this.update.emit(this.getAssigneesList());
  }

  /**
   * If available, it calls the save
   * callback to save the TI
   */
  saveAssignees() {
    if (this.allowSaving) {
      this.update.emit(this.getAssigneesList());
    }
  }

  /**
   * Updates assignees historic list with 
   * the current tracker item assignees
   */
  private addToAssigneesHistoryList() {
    this._assigneesHistoryList = _.unionBy(this.getAssigneesList(), this._assigneesHistoryList, 'id');
  }

  /**
   * Updates the current members list to be displayed in 
   * the selector with:
   *   - the currently and previously assigned members
   *   - the remaining members from the current members page
   * 
   * @param {Array} members 
   */
  private setMembersList(members: any[]) {
    const sortedAssignees = _.sortBy(this._assigneesHistoryList, ['firstname', 'lastname']);
    const sortedMembers = _.sortBy(members, ['firstname', 'lastname']);

    sortedAssignees.forEach((item) => { item._former = false; });
    sortedMembers.forEach((item) => { item._former = false; });

    this.membersList = _.unionBy(sortedAssignees, sortedMembers, 'id').map((u: any) => {
      u._former = !(_.find(this.members, { id: u.id }));
      return u;
    });

    _.remove(this.membersList, (member: any) => member.id <= 100);
  }

  private getAssigneesList() {
    return _.get(this.trackerItem, 'rel.assignees', []);
  }

  /**
   * Performs a search & filtering of members and 
   * updates the displayed members list
   */
  private doSearch(membersFilter: string): Promise<any> {
    return this.assigneeHelper.getFilteredAssignees(membersFilter, _.get(this.trackerItem, 'tracker.id', false))
      .then((data: any) => {
        // The box may have been edited or emptied while this search was pending or in flight; a stale
        // answer must not replace the list that edit already set.
        if (membersFilter === this.membersFilter) {
          this.setMembersList(data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  private init() {
    if (!_.isEmpty(this.trackerItem)) {
      if (_.has(this.trackerItem, 'rel') && !_.has(this.trackerItem.rel, 'assignees')) {
        this.assigneeHelper.emptyAssignees(this.trackerItem);
      }
    }
    this.allowUpdates = this.getAllowUpdates();
    this.setMembersList(this.members);
  }

  private getAllowUpdates(): boolean {
    return this.trackeritemService.allowUpdates(this.trackerItem);
  }

  private debouncePromise(fn: Function, wait: number, options?: any) {
    let timeout: any;
    let promise: Promise<any>;
    let resolve: Function;
    let reject: Function;

    return function(...args: any[]) {
      clearTimeout(timeout);

      if (!promise) {
        promise = new Promise((_resolve, _reject) => {
          resolve = _resolve;
          reject = _reject;
        });
      }

      timeout = setTimeout(() => {
        promise = null;
        resolve = null;
        reject = null;

        fn(...args)
          .then((result: any) => {
            if (resolve) resolve(result);
          })
          .catch((error: any) => {
            if (reject) reject(error);
          });
      }, wait);

      return promise;
    };
  }
}
